import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { API, IncomingClientConnectionEstablishmentRequestDTO } from '../../../../services/api/api';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';
import { ErrorService } from '../../../../services/error.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RequestClass } from '../../../../services/global/RequestClass';

type SplitButtonAction = 'accept' | 'delete';

@Component({
  selector: 'sb-table-client-connections-incoming',
  templateUrl: './client-connections-incomming-table.component.html',
  styleUrls: ['./client-connections-incomming-table.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncomingClientConnectionsTableComponent extends RequestClass {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  private readonly toaster = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly errorService = inject(ErrorService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToasterService);

  // table wrapper variables
  data: IncomingClientConnectionEstablishmentRequestDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  public refreshFromServer(): void {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_ClientConnection_retrieveIncomingClientConnectionEstablishmentRequests(
        {
          connected: false,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: requests => {
          this.data = this.buildSplitButtons(requests);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  public accept(connection: IncomingClientConnectionEstablishmentRequestDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.clientManagement.connection.accept'),
        message: this.translate.instant('lang.clientManagement.connection.accept.infoText', { clientName: connection.requesting.name }),
        yes: this.translate.instant('lang.clientManagement.connection.acceptAction'),
        no: this.translate.instant('lang.globals.cancel'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .shared_ClientConnection_acceptClientConnectionEstablishmentRequest(connection)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.connection.accepted'));
                this.refreshFromServer();
              },
              error: error => {
                this.errorService.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  /* decline endpoint */
  public delete(connection: IncomingClientConnectionEstablishmentRequestDTO): void {
    if (connection) {
      this.modalService
        .confirm({
          title: this.translate.instant('lang.clientManagement.connection.reject'),
          message: this.translate.instant('lang.clientManagement.connection.reject.infoText', { clientName: connection.requesting.name }),
          yes: this.translate.instant('lang.clientManagement.connection.rejectAction'),
          no: this.translate.instant('lang.globals.cancel'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .shared_ClientConnection_withdrawClientConnectionEstablishmentRequest(connection.requesting.id)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.connection.rejected'));
                  this.refreshFromServer();
                },
                error: error => {
                  this.errorService.errorHandler(error);
                },
              });
          }
        })
        .catch(() => {});
    }
  }

  buildSplitButtons<T>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'checked',
        iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
        action: 'accept',
        tooltip: 'lang.clientManagement.connection.accept',
      });
      item.sbButtons.push({
        icon: 'cancel',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.clientManagement.connection.reject',
      });
    });
    return result;
  }

  resolveSplitButtonAction(connection: IncomingClientConnectionEstablishmentRequestDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'accept':
        this.accept(connection);
        break;
      case 'delete':
        this.delete(connection);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
